import { useCallback, useEffect, useState } from 'react';
import { X, Calendar, Loader2, User, Trash2, Info } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { api } from '../../lib/api';
import type { ApiWorkItem, ApiWorkItemSchedulePhaseInput } from '../../lib/api';
import { LocationVerifyStep } from './LocationVerifyStep';
import { usePermissions } from '../../hooks/usePermissions';

type PhaseStatus = 'provisional' | 'confirmed';

/** Format a NUMERIC-as-money value (£) for the read-only Amount header + banner. */
function formatGbp(n: number): string {
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Per-phase form state.
 *
 * `formId` is a stable React key surviving add/delete reorders. Display
 * labels ("Phase N") and the `phase_number` sent to the backend are
 * derived from the array index at render/save time — pre-save deletions
 * renumber the remaining forms (Q9 stable numbering only applies once
 * Schedule Job is clicked; pre-save wizard state is form-builder mode
 * where deleted slots don't create audit-meaningful gaps).
 */
interface PhaseFormState {
  formId: string;
  startDate: string;
  endDate: string;
  notes: string;
  status: PhaseStatus;
  managerId: string;
  // Per-phase profitability: planner-set budget (£) as a raw string. Empty
  // string = unset → sent as null. Required (with notes) when a chargeable
  // work-item is scheduled with 2+ phases (backend enforces at confirm).
  phaseBudget: string;
}

let nextFormId = 0;
function defaultPhaseFormState(initial: { startDate?: string } = {}): PhaseFormState {
  nextFormId += 1;
  return {
    formId: `phase-form-${nextFormId}`,
    startDate: initial.startDate ?? format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    notes: '',
    // 80-02: tickets seed `provisional` (non_chargeable is gone from the phase
    // model); deals also default to provisional.
    status: 'provisional',
    managerId: '',
    phaseBudget: '',
  };
}

interface ScheduleModalProps {
  workItem: ApiWorkItem;
  onClose: () => void;
  // #100: when the modal is opened via a per-day calendar "+" button, the
  // clicked day (yyyy-MM-dd) seeds the first phase's start date instead of
  // today. Omitted for the normal Available-Work flow → defaults to today.
  initialStartDate?: string;
}

export function ScheduleModal(props: ScheduleModalProps) {
  const { can, isLoading } = usePermissions();
  const canScheduleWork = can('contracts_board', 'add_scheduled_work');
  const { onClose } = props;
  useEffect(() => {
    if (!isLoading && !canScheduleWork) onClose();
  }, [canScheduleWork, isLoading, onClose]);

  // Unmount all form/confirmation state on refreshed grant loss, even if a
  // caller keeps this wrapper mounted. The backend remains the security boundary.
  return canScheduleWork ? <ScheduleForm {...props} /> : null;
}

function ScheduleForm({ workItem, onClose, initialStartDate }: ScheduleModalProps) {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canScheduleWork = can('contracts_board', 'add_scheduled_work');
  const isTicket = workItem.source === 'hubspot_ticket';
  // Per-phase profitability money UI (Amount header, per-phase Budget inputs,
  // reconciliation banner) is gated on the planner-tier `update` permission —
  // hidden entirely for below-planner roles.
  const canSeeMoney = can('contracts_board', 'update');
  const [phases, setPhases] = useState<PhaseFormState[]>(() => [
    defaultPhaseFormState({ startDate: initialStartDate }),
  ]);
  const [hasClickedAddPhase, setHasClickedAddPhase] = useState(false);
  const [showSinglePhaseConfirm, setShowSinglePhaseConfirm] = useState(false);

  // ─── Phase 41-02 (#65): location verification pre-flight ──────────────────
  //
  // Pre-flight visibility per Q4:
  //   - Deals: always visible until site_address_verified=true
  //   - Tickets where ticket.location === 'Client Site Address': visible
  //     until linked_deal_site_address_verified=true (verified via the
  //     LINKED deal in HubSpot — ticket itself has no site_address)
  //   - Tickets with location in {G2, HQ, Employees Home Address}: hidden
  //     entirely — their geofence centre is fixed and doesn't need
  //     address verification
  //
  // For tickets the address to verify is linked_deal_site_address (the
  // customer's address from the LINKED deal in HubSpot); for deals it's
  // the work item's own site_address.
  const ticketLocationRaw =
    typeof workItem.source_metadata?.location === 'string'
      ? (workItem.source_metadata.location as string).trim().toLowerCase()
      : null;
  const ticketUsesClientSiteAddress = ticketLocationRaw === 'client site address';

  const verificationApplies = isTicket
    ? ticketUsesClientSiteAddress
    : workItem.source === 'hubspot';

  const initiallyVerified = isTicket
    ? !!workItem.linked_deal_site_address_verified
    : workItem.site_address_verified;

  const initialAddress = isTicket
    ? (workItem.linked_deal_site_address ?? '')
    : (workItem.site_address ?? '');

  const [verifiedAddress, setVerifiedAddress] = useState<string | null>(
    !verificationApplies || initiallyVerified ? initialAddress : null,
  );

  const handleVerified = useCallback((addr: string) => {
    setVerifiedAddress(addr);
  }, []);

  const verificationGate = verificationApplies && !verifiedAddress;

  const { data: allStaff } = useQuery({
    queryKey: ['staff'],
    queryFn: () => api.staff.list(),
  });
  const managers = (allStaff ?? []).filter(
    (s) => s.staff_role === 'manager' && !s.is_subcontractor && s.is_active,
  );

  function updatePhase(idx: number, partial: Partial<PhaseFormState>) {
    setPhases((current) => current.map((p, i) => (i === idx ? { ...p, ...partial } : p)));
  }

  function addPhase() {
    setHasClickedAddPhase(true);
    setPhases((current) => [...current, defaultPhaseFormState()]);
  }

  function removePhase(idx: number) {
    setPhases((current) => current.filter((_, i) => i !== idx));
  }

  // Coerce a PhaseFormState into the wire payload. 80-02: each phase posts the
  // single `status` field (provisional|confirmed). phase_number = array index +
  // 1 — pre-save deletions renumber freely (Q9 stable numbering only kicks in
  // once Schedule Job is clicked).
  function toPhaseInput(phase: PhaseFormState, idx: number): ApiWorkItemSchedulePhaseInput {
    return {
      phase_number: idx + 1,
      start_date: phase.startDate,
      end_date: phase.endDate || phase.startDate,
      contracts_manager_id: phase.managerId || null,
      status: phase.status === 'confirmed' ? 'confirmed' : 'provisional',
      notes: phase.notes.trim() || null,
      phase_budget: phase.phaseBudget.trim() === '' ? null : phase.phaseBudget.trim(),
    };
  }

  const mutation = useMutation({
    mutationFn: () => {
      if (!canScheduleWork) throw new Error('You do not have permission to schedule work.');
      if (phases.length === 1 && !hasClickedAddPhase) {
        // Legacy flat-fields path — single phase, planner never engaged
        // multi-phase mode. Preserves today's API shape for backwards-compat.
        const p = phases[0];
        return api.workItems.schedule(workItem.id, {
          startDate: p.startDate,
          endDate: p.endDate || undefined,
          notes: p.notes.trim() || undefined,
          contractsManagerId: p.managerId || undefined,
          status: p.status,
        });
      }
      // Multi-phase mode (or single-phase post-Add-Phase via confirmation).
      // Always send explicit phases array so the planner's display numbers
      // (which may have gaps after delete) are honoured.
      return api.workItems.schedule(workItem.id, {
        phases: phases.map(toPhaseInput),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['work-items'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      onClose();
    },
  });

  function handleScheduleClick() {
    if (!canScheduleWork) return;
    if (phases.length === 1 && hasClickedAddPhase) {
      setShowSinglePhaseConfirm(true);
      return;
    }
    mutation.mutate();
  }

  // ─── Per-phase profitability: Amount / budgets / reconciliation ───────────
  // Chargeable = deal/manual work-item (NOT a ticket). Money UI only renders
  // for chargeable items when the planner can see it.
  const isChargeable = !isTicket;
  const isPhased = phases.length >= 2;
  const showMoneyUi = isChargeable && canSeeMoney;
  // This equals jobs.hubspot_amount (copied onto the work_item's source_metadata
  // at schedule time in workItems.ts), which is exactly what the backend
  // mandatory phase-budget reconciliation checks against — so this banner's
  // balance maths and the confirm-gate agree on the same Amount.
  const amount = workItem.source_metadata?.amount ?? null;
  // Whole-job label so planners can't mistake the deal Amount for a phase
  // figure. ION should always be present for chargeable deal jobs; fall back
  // defensively if it isn't.
  const amountLabel = workItem.ion ? `Amount for job ${workItem.ion}` : 'Amount for this job';
  // Σ of the per-phase budgets (blank/invalid entries count as 0).
  const allocated = phases.reduce((sum, p) => {
    const v = parseFloat(p.phaseBudget);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0);
  const remaining = amount === null ? null : amount - allocated;
  const balanced = remaining !== null && Math.abs(remaining) < 0.005;

  // Backend requires phase_budget + non-empty notes on every phase when a
  // chargeable work-item is scheduled with 2+ phases. Mirror that client-side
  // so the planner can't submit an under-specified payload. Gated on
  // canSeeMoney because the budget input is hidden for below-planner roles —
  // we don't block on a field the user can't see.
  const phaseDetailRequired = isChargeable && isPhased && canSeeMoney;
  const phaseRequirementsUnmet =
    phaseDetailRequired && phases.some((p) => p.notes.trim() === '' || p.phaseBudget.trim() === '');

  const canSubmit =
    canScheduleWork &&
    !mutation.isPending &&
    !verificationGate &&
    !phaseRequirementsUnmet &&
    phases.every((p) => p.startDate && p.managerId) &&
    phases.length >= 1;

  const showPhaseLabels = phases.length >= 2;
  const showInfoBanner = hasClickedAddPhase;

  // Live reconciliation summary (advisory — does NOT block Schedule; the
  // backend enforces balance at confirm). Rendered above EACH phase's Budget
  // box so the running total stays in view while filling lower phases (#244).
  // Allocated/Remaining are derived from the current phase-budget inputs, so
  // every copy updates as the planner types.
  function renderReconciliationSummary() {
    return (
      <div
        className={
          balanced
            ? 'flex flex-wrap items-center gap-x-3 gap-y-1 bg-green-50 border border-green-200 text-green-800 rounded-md px-3 py-2 text-[12px]'
            : 'flex flex-wrap items-center gap-x-3 gap-y-1 bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2 text-[12px]'
        }
      >
        <span>
          {amountLabel} {amount === null ? 'not set' : formatGbp(amount)}
        </span>
        <span aria-hidden>·</span>
        <span>Allocated {formatGbp(allocated)}</span>
        <span aria-hidden>·</span>
        <span>Remaining {remaining === null ? '—' : formatGbp(remaining)}</span>
        <span className="font-semibold">
          {balanced
            ? 'balances ✓'
            : remaining === null
              ? 'Amount not set'
              : remaining > 0
                ? `${formatGbp(remaining)} unallocated`
                : `${formatGbp(-remaining)} over`}
        </span>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-white rounded-xl shadow-2xl border border-neutral-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-neutral-200">
          <div>
            <p className="text-[11px] font-mono text-neutral-400">{workItem.ion ?? 'No ION'}</p>
            <h2 className="text-[15px] text-neutral-900 mt-0.5 leading-snug">{workItem.name}</h2>
            {workItem.client_name && (
              <p className="text-[12px] text-neutral-500 mt-0.5">{workItem.client_name}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-100 rounded-md text-neutral-400 transition-colors mt-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form — scrollable when multi-phase grows tall */}
        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
          {/* Phase 41-02 (#65): location verification pre-flight (closes the
              gate above the form). Invisible when verificationApplies=false
              (e.g. tickets where location=G2/HQ/Employees Home Address). */}
          {verificationApplies && (
            <LocationVerifyStep
              workItemId={workItem.id}
              initialAddress={initialAddress}
              initiallyVerified={initiallyVerified}
              onVerified={handleVerified}
            />
          )}

          {showInfoBanner && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2 text-[12px]">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Adding phases transfers date authority from HubSpot to CB — HubSpot date changes
                will no longer sync to this job.
              </span>
            </div>
          )}

          {/* Per-phase profitability: read-only Amount (whole-job deal value)
              header. Labelled with the ION so it reads as the job figure, not a
              phase budget (#244). The live reconciliation summary is rendered
              per-phase (above each Budget box) rather than once here, so it
              stays in view while editing lower phases. */}
          {showMoneyUi && (
            <div className="flex items-center justify-between bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2">
              <span className="text-[12px] text-neutral-500">{amountLabel}</span>
              <span className="text-[13px] font-semibold text-neutral-900">
                {amount === null ? 'Amount not set' : formatGbp(amount)}
              </span>
            </div>
          )}

          {phases.map((phase, idx) => (
            <div
              key={phase.formId}
              className={
                showPhaseLabels
                  ? 'space-y-4 pb-4 border-b border-neutral-100 last:border-b-0 last:pb-0'
                  : 'space-y-4'
              }
            >
              {showPhaseLabels && (
                <div className="flex items-center justify-between">
                  <h3 className="text-[13px] font-semibold text-neutral-700">Phase {idx + 1}</h3>
                  {phases.length > 1 && (
                    <button
                      onClick={() => removePhase(idx)}
                      aria-label={`Delete Phase ${idx + 1}`}
                      className="p-1 hover:bg-red-50 rounded-md text-neutral-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}

              <div>
                <label className="block text-[12px] text-neutral-600 mb-1.5">
                  Contracts Manager <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
                  <select
                    value={phase.managerId}
                    onChange={(e) => updatePhase(idx, { managerId: e.target.value })}
                    disabled={verificationGate}
                    className="w-full pl-8 pr-3 py-2 text-[13px] border border-neutral-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522] bg-white appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a manager...</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.known_as || m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {!isTicket && (
                <div>
                  <label className="block text-[12px] text-neutral-600 mb-1.5">Status</label>
                  <select
                    value={phase.status}
                    onChange={(e) => updatePhase(idx, { status: e.target.value as PhaseStatus })}
                    disabled={verificationGate}
                    className="w-full px-3 py-2 text-[13px] border border-neutral-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522] bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="provisional">Provisional</option>
                    <option value="confirmed">Confirmed</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-[12px] text-neutral-600 mb-1.5">
                  Start date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
                  <input
                    type="date"
                    value={phase.startDate}
                    onChange={(e) => updatePhase(idx, { startDate: e.target.value })}
                    disabled={verificationGate}
                    className="w-full pl-8 pr-3 py-2 text-[13px] border border-neutral-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522] disabled:opacity-50 disabled:cursor-not-allowed"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[12px] text-neutral-600 mb-1.5">
                  End date
                  <span className="text-neutral-400 ml-1">
                    (optional — leave blank for single day)
                  </span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
                  <input
                    type="date"
                    value={phase.endDate}
                    min={phase.startDate}
                    onChange={(e) => updatePhase(idx, { endDate: e.target.value })}
                    disabled={verificationGate}
                    className="w-full pl-8 pr-3 py-2 text-[13px] border border-neutral-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Per-phase profitability: live reconciliation summary repeated
                  directly above this phase's Budget box so the running total
                  (Amount / Allocated / Remaining) stays in view while editing
                  lower phases (#244). Same gating as the Budget input. */}
              {showMoneyUi && isPhased && renderReconciliationSummary()}

              {/* Per-phase profitability: Budget (£) input. Only on chargeable
                  multi-phase schedules when the planner can see money UI. */}
              {showMoneyUi && isPhased && (
                <div>
                  <label className="block text-[12px] text-neutral-600 mb-1.5">
                    Budget (£) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={phase.phaseBudget}
                    onChange={(e) => updatePhase(idx, { phaseBudget: e.target.value })}
                    placeholder="0.00"
                    disabled={verificationGate}
                    className="w-full px-3 py-2 text-[13px] border border-neutral-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              )}

              <div>
                <label className="block text-[12px] text-neutral-600 mb-1.5">
                  Detail / Scope{' '}
                  {phaseDetailRequired ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-neutral-400">(optional)</span>
                  )}
                </label>
                <textarea
                  value={phase.notes}
                  onChange={(e) => updatePhase(idx, { notes: e.target.value })}
                  placeholder="Any scheduling notes…"
                  rows={2}
                  disabled={verificationGate}
                  className="w-full text-[13px] border border-neutral-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522] disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          ))}

          {phaseRequirementsUnmet && (
            <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Each phase needs a Budget (£) and a Detail / Scope before scheduling.
            </p>
          )}

          {mutation.isError && (
            <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {mutation.error instanceof Error ? mutation.error.message : 'Failed to schedule job'}
            </p>
          )}
        </div>

        {/* Footer — Add Phase pinned bottom-left so it's always visible
            regardless of how long the form grows (Spec [2] amendment 2026-05-21
            from UAT — the in-content text link was getting clipped below the
            fold on multi-phase wizards). */}
        <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between rounded-b-xl">
          <button
            onClick={addPhase}
            className="text-[12px] text-neutral-500 hover:text-neutral-700 underline-offset-2 hover:underline transition-colors"
          >
            + Add Phase (optional)
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-[13px] border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleScheduleClick}
              disabled={!canSubmit}
              className="flex items-center gap-1.5 px-4 py-2 text-[13px] bg-[#F26522] text-white rounded-md hover:bg-[#d9561d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {mutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Schedule Job
            </button>
          </div>
        </div>
      </div>

      {/* Single-phase confirmation modal (per spec §3.1) */}
      {showSinglePhaseConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[60]"
            onClick={() => setShowSinglePhaseConfirm(false)}
          />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-sm bg-white rounded-xl shadow-2xl border border-neutral-200 p-5">
            <h3 className="text-[14px] font-semibold text-neutral-900 mb-2">
              Only one phase has been entered
            </h3>
            <p className="text-[13px] text-neutral-600 mb-5">
              Would you like to save this as a standard job instead, or add another phase?
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  if (!canScheduleWork) return;
                  setShowSinglePhaseConfirm(false);
                  mutation.mutate();
                }}
                disabled={!canSubmit}
                className="px-4 py-2 text-[13px] bg-[#F26522] text-white rounded-md hover:bg-[#d9561d] transition-colors"
              >
                Save as standard job
              </button>
              <button
                onClick={() => {
                  setShowSinglePhaseConfirm(false);
                  addPhase();
                }}
                className="px-4 py-2 text-[13px] border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-100 transition-colors"
              >
                Add another phase
              </button>
              <button
                onClick={() => setShowSinglePhaseConfirm(false)}
                className="px-4 py-2 text-[13px] text-neutral-500 hover:bg-neutral-100 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
